import { Ticker, Loader } from 'pixi.js';
import { random } from 'lodash';
import eventHub from '@/scripts/eventHub';
import Flower from '../actors/Flower';
import vars from '../vars';
import state from '../state';
import PowerUp from '../actors/PowerUp';
import SuperFlower from '../actors/SuperFlower';

class FlowerGenerator {
  constructor(container, hitArea) {
    this.container = container;
    this.hitArea = hitArea;
    this.time = 0;
    this.index = Number.MAX_SAFE_INTEGER;
    this.flowers = [];
    this.flowerSchema = new Map();
    const flowerSheet = Loader.shared.resources['images/FLOWERS'].spritesheet.textures;
    const powerupSheet = Loader.shared.resources['images/POWERUPS'].spritesheet.textures;
    this.flowerSchema = [{
      id: 'yellow',
      get textures() {
        return [
          flowerSheet['flower-yellow_1.png'],
          flowerSheet['flower-yellow_2.png'],
          flowerSheet['flower-yellow_3.png'],
          flowerSheet['flower-yellow_4.png'],
          flowerSheet['flower-yellow_5.png'],
        ];
      },
    }, {
      id: 'blue',
      get textures() {
        return [
          flowerSheet['flower-blue_1.png'],
          flowerSheet['flower-blue_2.png'],
          flowerSheet['flower-blue_3.png'],
          flowerSheet['flower-blue_4.png'],
          flowerSheet['flower-blue_5.png'],
        ];
      },
    }, {
      id: 'pink',
      get textures() {
        return [
          flowerSheet['flower-pink_1.png'],
          flowerSheet['flower-pink_2.png'],
          flowerSheet['flower-pink_3.png'],
          flowerSheet['flower-pink_4.png'],
          flowerSheet['flower-pink_5.png'],
        ];
      },
    }, {
      id: 'red',
      get textures() {
        return [
          flowerSheet['flower-red_1.png'],
          flowerSheet['flower-red_2.png'],
          flowerSheet['flower-red_3.png'],
          flowerSheet['flower-red_4.png'],
          flowerSheet['flower-red_5.png'],
        ];
      },
    }, {
      id: 'violet',
      get textures() {
        return [
          flowerSheet['flower-violet_1.png'],
          flowerSheet['flower-violet_2.png'],
          flowerSheet['flower-violet_3.png'],
          flowerSheet['flower-violet_4.png'],
          flowerSheet['flower-violet_5.png'],
        ];
      },
    }];

    this.poweUpSchema = [{
      id: 'power',
      get textures() {
        return [
          powerupSheet['powerup_power_1.png'],
          powerupSheet['powerup_power_2.png'],
          powerupSheet['powerup_power_3.png'],
          powerupSheet['powerup_power_4.png'],
          powerupSheet['powerup_power_5.png'],
          powerupSheet['powerup_power_6.png'],
        ];
      },
    }, {
      id: 'wide',
      get textures() {
        return [
          powerupSheet['powerup_wide_1.png'],
          powerupSheet['powerup_wide_2.png'],
          powerupSheet['powerup_wide_3.png'],
          powerupSheet['powerup_wide_4.png'],
          powerupSheet['powerup_wide_5.png'],
          powerupSheet['powerup_wide_6.png'],
        ];
      },
    }, {
      id: 'far',
      get textures() {
        return [
          powerupSheet['powerup_far_1.png'],
          powerupSheet['powerup_far_2.png'],
          powerupSheet['powerup_far_3.png'],
          powerupSheet['powerup_far_4.png'],
          powerupSheet['powerup_far_5.png'],
          powerupSheet['powerup_far_6.png'],
        ];
      },
    }];

    this.frameHandler = () => { this.frame(); };

    eventHub.$on('blow-all', () => { this.blowAll(); });

    this.container.sortableChildrens = true;
    this.resize();
  }

  resize() {
    this.left = this.container.position.x - (this.container.width / 2);
    this.right = this.container.position.x + (this.container.width / 2);
  }

  start() {
    Ticker.shared.add(this.frameHandler);
  }

  stop() {
    Ticker.shared.remove(this.frameHandler);
    for (let i = this.flowers.length - 1; i >= 0; i -= 1) {
      if (this.flowers[i]) {
        this.flowers[i].catch(); // Catchit 'case points are not used anymore
        this.flowers.splice(i, 1);
      }
    }
  }

  frame() {
    this.time += Ticker.shared.deltaMS;
    if (this.time > state.spawnDelay) {
      this.spawnFlower();
      this.time = 0;
    }

    const squareRect = this.hitArea.hitSquare.getBounds();
    for (let i = this.flowers.length - 1; i >= 0; i -= 1) {
      const f = this.flowers[i];

      // Remove from array if already catched
      if (f.catched) {
        this.flowers.splice(i, 1);
        return;
      }

      // Now check for collisions
      const flowerRect = f.sprite.getBounds();
      if (
        state.rayActive
        && (flowerRect.y + flowerRect.height) >= squareRect.y
        && flowerRect.y <= squareRect.y + squareRect.height
        && (flowerRect.x + flowerRect.width) >= squareRect.x
        && flowerRect.x <= (squareRect.x + squareRect.width)
      ) {
        f.catching = true;
      } else {
        f.catching = false;
      }

      // Check if over limit and missed
      if ((flowerRect.y + flowerRect.height) > vars.layout.BOTTOM_LIMIT) {
        f.catching = false;
        f.miss();
        this.flowers.splice(i, 1);
      }
    }
  }

  spawnFlower() {
    this.index -= 1;
    const baseY = -1 * ((vars.layout.STAGE_HEIGHT * 2.5) / 4);
    const randomX = random(this.left, this.right, false);
    const poweupProbability = 0.05;
    const superFlowerProbability = 0.01;
    const isSuperFlower = random(0, 1, true) < superFlowerProbability;
    if (isSuperFlower) {
      const flower = new SuperFlower(this.container, randomX, baseY);
      flower.ref.zIndex = this.index;
      this.flowers.push(flower);
      flower.start();
    } else {
      const isPowerup = state.hasActivePowerUp ? false : random(0, 1, true) < poweupProbability;
      if (!isPowerup) {
        const randomType = random(0, this.flowerSchema.length - 1, false);
        const type = this.flowerSchema[randomType];
        const flower = new Flower(this.container, randomX, baseY, type.id, type.textures);
        flower.ref.zIndex = this.index;
        this.flowers.push(flower);
        flower.start();
      } else {
        const randomType = random(0, this.poweUpSchema.length - 1, false);
        const type = this.poweUpSchema[randomType];
        const powerup = new PowerUp(this.container, randomX, baseY, type.id, type.textures);
        powerup.ref.zIndex = this.index;
        this.flowers.push(powerup);
        powerup.start();
      }
    }

    this.container.sortChildren();
  }

  blowAll() {
    for (let i = this.flowers.length - 1; i >= 0; i -= 1) {
      if (!this.flowers[i].catched) {
        this.flowers[i].muted = true;
        this.flowers[i].catching = true;
        this.flowers.splice(i, 1);
      }
    }
  }
}

export default FlowerGenerator;
